import { Button, Flex, Heading } from "@chakra-ui/react";
import { FieldArray, useFormikContext } from "formik";
import { SelectControl } from "formik-chakra-ui";
import { RiAddLine, RiDeleteBin6Line } from "react-icons/ri";
import { InputControlWithMaxLength } from "src/components/Inputs/InputControlWithMaxLength";
import { LexicalControlWithMaxLength } from "src/components/Inputs/LexicalControlWithMaxLength";
import { AdminCard } from "src/components/Layout/AdminCard";
import { ConditionalTooltip } from "src/components/Layout/ConditionalTooltip";
import * as OrgConfig from "src/generated/hooks/org-config/requests";
import { useDeleteConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { isNonEmptyString } from "src/services/predicates";
import {
  FamilyContent,
  FamilyContentKind,
  FormikType,
  getDefaultFamilyContent,
  MAX_BUTTON_TEXT_LENGTH,
  MAX_CUSTOM_CONTENT_LENGTH,
  MAX_DESCRIPTION_LENGTH,
  MAX_TITLE_LENGTH,
} from "./Formik";

type FamilyOption = { value: FamilyContentKind; label: string };
const familyCardOptions: FamilyOption[] = [
  {
    value: "family-login-button",
    label: "Family log in screen",
  },
  {
    value: "family-signup-button",
    label: "Family sign up screen",
  },
  {
    value: "account-lookup-button",
    label: "Account lookup",
  },
  {
    value: "custom-content",
    label: "Custom content",
  },
];

function isFamilyOptionAvailable(
  value: string,
  index: number,
  formikValue: FormikType,
  accountLookup: OrgConfig.AccountLookupConfig | null
): boolean {
  if (value === formikValue.familyCard.contents[index]?.kind) {
    return true;
  }

  const alreadyUsed =
    formikValue.familyCard.contents.findIndex(
      (content) => content.kind === value
    ) >= 0;

  if (accountLookup?.disabled !== false && value === "account-lookup-button") {
    return false;
  }

  return !alreadyUsed;
}

function firstAvailableFamilyOption(
  formikValue: FormikType,
  accountLookup: OrgConfig.AccountLookupConfig | null
): FamilyContentKind | null {
  return (
    familyCardOptions.find((option) =>
      isFamilyOptionAvailable(option.value, -1, formikValue, accountLookup)
    )?.value ?? null
  );
}

type Props = { accountLookup: OrgConfig.AccountLookupConfig | null };
export const FamilyCard: React.FC<Props> = ({ accountLookup }) => {
  const formik = useFormikContext<FormikType>();
  const addFamilyContentButtonDisabled = !isNonEmptyString(
    firstAvailableFamilyOption(formik.values, accountLookup)
  );
  const { confirm, confirmationDialog } = useDeleteConfirmationDialog({
    header: "Are you sure you want to delete this content?",
  });

  return (
    <AdminCard
      title={
        <Heading as="h3" variant="admin">
          Family welcome card
        </Heading>
      }
      description="Provide guidance to families looking for their account or where to sign up."
    >
      <Flex direction="column" gap="4">
        <InputControlWithMaxLength
          label="Title"
          id="familyCard.title"
          name="familyCard.title"
          maxLength={MAX_TITLE_LENGTH}
          inputProps={{
            placeholder: "E.g.: Families",
          }}
        />
        {/* We don't support rich content for description yet, but I foresee that this request will come soon, so I'm preparing for it by using lexical with hidden toolbar for now. */}
        <LexicalControlWithMaxLength
          label="Description"
          name="familyCard.description"
          maxLength={MAX_DESCRIPTION_LENGTH}
          plainFieldName="familyCard.plainDescription"
          placeholder="E.g.: Parents and guardians applying to schools or checking in."
          format="html"
          height="5rem"
          settings={{
            hideToolbar: true,
            enableSmsStats: false,
            enableVariables: false,
            enableAutofocus: false,
            enableBlockFormat: false,
            enableElementFormat: false,
            enableTextFormat: false,
            enableImage: false,
            enableCode: false,
            enableHorizontalRule: false,
            enableLink: false,
          }}
        />
        <FieldArray name="familyCard.contents">
          {(arrayHelpers) => (
            <>
              {formik.values.familyCard.contents.map((content, index) => (
                <AdminCard title="" key={index}>
                  <Flex direction="column" gap="4">
                    <SelectControl
                      label="Destination"
                      name={`familyCard.contents.${index}.kind`}
                      selectProps={{
                        onChange: (event) => {
                          const kind = event.target.value as FamilyContentKind;
                          arrayHelpers.replace(index, {
                            kind,
                            content: getDefaultFamilyContent(kind),
                            plainContent: "",
                          });
                        },
                      }}
                    >
                      {familyCardOptions.map((option) =>
                        isFamilyOptionAvailable(
                          option.value,
                          index,
                          formik.values,
                          accountLookup
                        ) ? (
                          <option value={option.value} key={option.value}>
                            {option.label}
                          </option>
                        ) : null
                      )}
                    </SelectControl>
                    {content.kind === "custom-content" ? (
                      <LexicalControlWithMaxLength
                        format="html"
                        label="Content"
                        name={`familyCard.contents.${index}.content`}
                        plainFieldName={`familyCard.contents.${index}.plainContent`}
                        width="100%"
                        height="5rem"
                        editorVersion="full"
                        settings={{
                          enableSmsStats: false,
                          enableVariables: false,
                          enableAutofocus: false,
                          enableBlockFormat: false,
                          enableElementFormat: false,
                          enableTextFormat: false,
                          enableImage: false,
                          enableCode: false,
                          enableHorizontalRule: false,
                        }}
                        maxLength={MAX_CUSTOM_CONTENT_LENGTH}
                      />
                    ) : (
                      <InputControlWithMaxLength
                        label="Text"
                        name={`familyCard.contents.${index}.content`}
                        maxLength={MAX_BUTTON_TEXT_LENGTH}
                      />
                    )}
                    {index > 0 ? (
                      <Button
                        leftIcon={<RiDeleteBin6Line />}
                        variant="outline"
                        alignSelf="flex-end"
                        colorScheme="gray"
                        onClick={async () => {
                          if (!(await confirm())) {
                            return;
                          }
                          arrayHelpers.remove(index);
                        }}
                      >
                        Remove content
                      </Button>
                    ) : null}
                  </Flex>
                </AdminCard>
              ))}
              <ConditionalTooltip
                label="Maximum amount of content reached"
                showTooltip={addFamilyContentButtonDisabled}
              >
                <Button
                  isDisabled={addFamilyContentButtonDisabled}
                  leftIcon={<RiAddLine />}
                  variant="outline"
                  colorScheme="gray"
                  borderStyle="dashed"
                  onClick={() => {
                    const kind = firstAvailableFamilyOption(
                      formik.values,
                      accountLookup
                    );
                    if (isNonEmptyString(kind)) {
                      return arrayHelpers.insert(
                        formik.values.familyCard.contents.length,
                        {
                          kind,
                          content: getDefaultFamilyContent(kind),
                          plainContent: "",
                        } satisfies FamilyContent
                      );
                    }
                  }}
                >
                  Add content
                </Button>
              </ConditionalTooltip>
            </>
          )}
        </FieldArray>
      </Flex>
      {confirmationDialog}
    </AdminCard>
  );
};
